import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parse } from 'date-fns';
import { Trash2, AlertTriangle, CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { getDateLocale } from '@/utils/dateLocale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { TimePicker } from '@/components/ui/time-picker';
import { Label } from '@/components/ui/label';
import type { AvailabilityOverride } from '@/services/api/availability.service';

export interface ServiceOption {
  id: number;
  name: string;
  active?: boolean;
}

export interface UpdateOverrideData {
  date: string;
  start_time: string;
  end_time: string;
  reason?: string;
  service_ids?: number[];
}

interface EditOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  override: AvailabilityOverride | null;
  services?: ServiceOption[];
  onUpdate: (id: number, data: UpdateOverrideData) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

export function EditOverrideDialog({
  open,
  onOpenChange,
  override,
  services = [],
  onUpdate,
  onDelete,
}: EditOverrideDialogProps) {
  const { t } = useTranslation('staff');

  // Form state
  const [date, setDate] = useState<Date | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Track which override we've initialized
  const initializedIdRef = useRef<number | null>(null);

  // Initialize form from override
  useEffect(() => {
    if (open && override && override.id !== initializedIdRef.current) {
      setDate(override.date ? parse(override.date, 'yyyy-MM-dd', new Date()) : null);
      setStartTime(override.start_time || '09:00');
      setEndTime(override.end_time || '17:00');
      setReason(override.reason || '');
      setSelectedServiceIds(override.service_ids || []);
      initializedIdRef.current = override.id;
    }
  }, [open, override]);

  // Reset all form + UI state when dialog closes.
  useEffect(() => {
    if (!open) {
      initializedIdRef.current = null;
      setDate(null);
      setCalendarOpen(false);
      setStartTime('09:00');
      setEndTime('17:00');
      setReason('');
      setSelectedServiceIds([]);
      setIsSubmitting(false);
      setShowDeleteConfirm(false);
    }
  }, [open]);

  // Early return after hooks
  if (!override) return null;

  // Validation
  const isValid = !!date && startTime < endTime && (services.length === 0 || selectedServiceIds.length > 0);

  const handleSave = async () => {
    if (isSubmitting || !isValid) return;
    setIsSubmitting(true);
    try {
      await onUpdate(override.id, {
        date: format(date!, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: endTime,
        reason: reason || undefined,
        service_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update override:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onDelete(override.id);
      setShowDeleteConfirm(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to delete override:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('availability.editOverride.title', 'Edit One-Time Availability')}</DialogTitle>
            <DialogDescription>
              {t('availability.editOverride.description', 'Modify this one-time availability')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Date */}
            <div className="space-y-2">
              <Label>{t('availability.dialog.date', 'Date')}</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !date && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date
                      ? format(date, 'EEEE, MMM d, yyyy', { locale: getDateLocale() })
                      : t('availability.dialog.pickDate', 'Pick a date')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date || undefined}
                    onSelect={(d) => {
                      setDate(d || null);
                      setCalendarOpen(false);
                    }}
                    initialFocus
                    locale={getDateLocale()}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('availability.dialog.startTime', 'Start time')}</Label>
                <TimePicker
                  value={startTime}
                  onChange={setStartTime}
                  placeholder={t('availability.dialog.selectStartTime', 'Select start time')}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('availability.dialog.endTime', 'End time')}</Label>
                <TimePicker
                  value={endTime}
                  onChange={setEndTime}
                  placeholder={t('availability.dialog.selectEndTime', 'Select end time')}
                />
              </div>
            </div>

            {/* Time validation error */}
            {startTime && endTime && startTime >= endTime && (
              <p className="text-sm text-red-500">
                {t('availability.edit.timeError', 'End time must be after start time')}
              </p>
            )}

            {/* Reason field */}
            <div className="space-y-2">
              <Label>{t('availability.editOverride.reason', 'Reason (optional)')}</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('availability.editOverride.reasonPlaceholder', 'e.g., Special event, extra hours')}
              />
            </div>

            {/* Service selection */}
            {services.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('availability.dialog.services', 'Services')}</Label>
                  {selectedServiceIds.length > 0 && (
                    <span className="text-xs text-blue-600 font-medium">
                      {t('availability.dialog.selectedServices', '{{count}} selected', { count: selectedServiceIds.length })}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {services.map((service) => (
                    <label
                      key={service.id}
                      htmlFor={`edit-override-service-${service.id}`}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors min-h-[44px]",
                        selectedServiceIds.includes(service.id)
                          ? "bg-blue-50 border-blue-300"
                          : "bg-white border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <Checkbox
                        id={`edit-override-service-${service.id}`}
                        checked={selectedServiceIds.includes(service.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedServiceIds([...selectedServiceIds, service.id]);
                          } else {
                            setSelectedServiceIds(selectedServiceIds.filter(id => id !== service.id));
                          }
                        }}
                      />
                      <span className="text-sm">
                        {service.name}
                        {service.active === false && (
                          <span className="ml-1 text-xs text-gray-400">{t('availability.dialog.inactive', '(inactive)')}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="space-y-3 pt-2 border-t">
            {/* Primary actions */}
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full sm:flex-1"
              >
                {t('availability.edit.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!isValid || isSubmitting}
                className="w-full sm:flex-1"
              >
                {isSubmitting ? t('availability.edit.saving', 'Saving...') : t('availability.edit.save', 'Save Changes')}
              </Button>
            </DialogFooter>

            {/* Destructive action */}
            <Button
              variant="ghost"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('availability.editOverride.delete', 'Delete One-Time Availability')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {t('availability.editOverride.deleteTitle', 'Delete One-Time Availability?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('availability.editOverride.deleteDescription', 'This will remove this one-time availability. This action cannot be undone.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              {t('availability.edit.cancelDelete', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? t('availability.edit.deleting', 'Deleting...') : t('availability.edit.deleteButton', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
